import React, { useEffect, useState } from 'react';
import axios from '../api/axios';
import styles from './MembersDirectory.module.css';
import { useAuth } from '../context/AuthContext';
import { getLevel, formatLevelBadge } from '../utils/levels';
import { Medal } from 'lucide-react';

interface Member {
  id: number;
  username: string;
  avatarUrl?: string;
  role?: string;
  primaryPositionTitle?: string;
  secondaryPositionTitle?: string;
  tertiaryPositionTitle?: string;
  department?: string;
  isOnline?: boolean;
  joinDate: string;
  flags?: string[];
  orgPoints?: number;
  profileTheme?: string;
  commendationsCount?: number;
}

const MembersDirectory: React.FC = () => {
  const { user } = useAuth();
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('all');
  const [filterRole, setFilterRole] = useState('all');
  const [filterLevel, setFilterLevel] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  useEffect(() => {
    const fetchMembers = async () => {
      try {
        const response = await axios.get('/auth/user/directory');
        console.log('Members fetched:', response.data);
        console.log('Total members count:', response.data.length);
        setMembers(response.data);
      } catch (error: any) {
        console.error('Error fetching members:', error);
        console.error('Error response:', error.response?.data);
        console.error('Error status:', error.response?.status);
      } finally {
        setLoading(false);
      }
    };

    fetchMembers();
  }, []);

  const getFullAvatarUrl = (avatarUrl?: string) => {
    if (!avatarUrl) return 'https://storage.tficorg.org/DefaultIcon.png';
    if (avatarUrl.startsWith('http')) return avatarUrl;
    return `${process.env.REACT_APP_API_URL}${avatarUrl}`;
  };

  const getAvatarBorderColor = (theme?: string): string => {
    switch (theme) {
      case 'elite-blue': return '#3b82f6';
      case 'master-gold': return '#f59e0b';
      case 'legend-purple': return '#a855f7';
      default: return 'var(--border-color)';
    }
  };

  const getFilteredMembers = () => {
    return members.filter(member => {
      const matchesSearch = 
        member.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
        member.primaryPositionTitle?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        member.secondaryPositionTitle?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        member.tertiaryPositionTitle?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesDepartment = 
        filterDepartment === 'all' || 
        member.department === filterDepartment;

      const matchesRole = 
        filterRole === 'all' || 
        member.role === filterRole;

      const matchesLevel = filterLevel === 'all' || (() => {
        const points = member.orgPoints || 0;
        const level = getLevel(points).level;
        return level === parseInt(filterLevel);
      })();

      return matchesSearch && matchesDepartment && matchesRole && matchesLevel;
    });
  };

  const getDepartments = () => {
    const depts = new Set<string>();
    members.forEach(member => {
      if (member.department) depts.add(member.department);
    });
    return Array.from(depts).sort();
  };

  if (loading) {
    return <div className={styles.loading}>Loading members...</div>;
  }

  const filteredMembers = getFilteredMembers();

  return (
    <div className={styles.pageContainer}>
      <div className={styles.card}>
        <div className={styles.header}>
          <h1 className={styles.title}>Member Directory</h1>
          <p className={styles.subtitle}>
            Connect with {members.length} organization members
          </p>
        </div>

        {/* Filters */}
        <div className={styles.filterBar}>
        <input
          type="text"
          placeholder="Search by username or position..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className={styles.searchInput}
        />

        <select
          value={filterDepartment}
          onChange={(e) => setFilterDepartment(e.target.value)}
          className={styles.filterSelect}
        >
          <option value="all">All Departments</option>
          {getDepartments().map(dept => (
            <option key={dept} value={dept}>{dept}</option>
          ))}
        </select>

        <select
          value={filterRole}
          onChange={(e) => setFilterRole(e.target.value)}
          className={styles.filterSelect}
        >
          <option value="all">All Roles</option>
          <option value="Admin">Admin</option>
          <option value="Leader">Leader</option>
          <option value="Member">Member</option>
          <option value="Prospect">Prospect</option>
        </select>

        <select
          value={filterLevel}
          onChange={(e) => setFilterLevel(e.target.value)}
          className={styles.filterSelect}
        >
          <option value="all">All Levels</option>
          <option value="0">Level 0 - Applicant</option>
          <option value="1">Level 1 - Junior Contributor</option>
          <option value="2">Level 2 - Contributor</option>
          <option value="3">Level 3 - Skilled Contributor</option>
          <option value="4">Level 4 - Senior Contributor</option>
          <option value="5">Level 5 - Distinguished Contributor</option>
          <option value="6">Level 6 - Veteran</option>
          <option value="7">Level 7 - Elite</option>
          <option value="8">Level 8 - Master</option>
          <option value="9">Level 9 - Legend</option>
          <option value="10">Level 10 - Mythic</option>
        </select>

        <div className={styles.viewToggle}>
          <button
            className={`${styles.viewBtn} ${viewMode === 'grid' ? styles.active : ''}`}
            onClick={() => setViewMode('grid')}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="7" height="7"></rect>
              <rect x="14" y="3" width="7" height="7"></rect>
              <rect x="14" y="14" width="7" height="7"></rect>
              <rect x="3" y="14" width="7" height="7"></rect>
            </svg>
          </button>
          <button
            className={`${styles.viewBtn} ${viewMode === 'list' ? styles.active : ''}`}
            onClick={() => setViewMode('list')}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="8" y1="6" x2="21" y2="6"></line>
              <line x1="8" y1="12" x2="21" y2="12"></line>
              <line x1="8" y1="18" x2="21" y2="18"></line>
              <line x1="3" y1="6" x2="3.01" y2="6"></line>
              <line x1="3" y1="12" x2="3.01" y2="12"></line>
              <line x1="3" y1="18" x2="3.01" y2="18"></line>
            </svg>
          </button>
        </div>
      </div>

      <div className={styles.resultCount}>
        Showing {filteredMembers.length} of {members.length} members
      </div>

      {/* Grid View */}
      {viewMode === 'grid' && (
        <div className={styles.memberGrid}>
          {filteredMembers.map(member => (
            <div key={member.id} className={styles.memberCard}>
              <div className={styles.avatarWrapper}>
                <img
                  src={getFullAvatarUrl(member.avatarUrl)}
                  alt={member.username}
                  className={styles.avatar}
                  style={{ borderColor: getAvatarBorderColor(member.profileTheme) }}
                />
                {member.isOnline && <div className={styles.onlineBadge}></div>}
              </div>
              <h3 className={styles.memberName}>{member.username}</h3>
              <div className={styles.levelBadge}>
                Level {getLevel(member.orgPoints || 0).level} - {formatLevelBadge(getLevel(member.orgPoints || 0).level)}
              </div>
              {member.commendationsCount !== undefined && member.commendationsCount > 0 && (
                <div className={styles.commendationsBadge}>
                  <Medal size={16} /> {member.commendationsCount} Commendation{member.commendationsCount !== 1 ? 's' : ''}
                </div>
              )}
              {member.department && (
                <div className={styles.department}>{member.department}</div>
              )}
              <div className={styles.positions}>
                {[
                  member.primaryPositionTitle,
                  member.secondaryPositionTitle,
                  member.tertiaryPositionTitle
                ].filter(Boolean).map((position, idx) => (
                  <span key={idx} className={styles.positionTag}>
                    {position}
                  </span>
                ))}
                {!member.primaryPositionTitle && !member.secondaryPositionTitle && !member.tertiaryPositionTitle && (
                  <span className={styles.noPosition}>No position assigned</span>
                )}
              </div>
              <div className={styles.memberSince}>
                Member since {new Date(member.joinDate).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* List View */}
      {viewMode === 'list' && (
        <div className={styles.memberList}>
          {filteredMembers.map(member => (
            <div key={member.id} className={styles.memberListItem}>
              <div className={styles.listItemLeft}>
                <div className={styles.avatarWrapper}>
                  <img
                    src={getFullAvatarUrl(member.avatarUrl)}
                    alt={member.username}
                    className={styles.avatarSmall}
                    style={{ borderColor: getAvatarBorderColor(member.profileTheme) }}
                  />
                  {member.isOnline && <div className={styles.onlineBadgeSmall}></div>}
                </div>
                <div>
                  <h3 className={styles.listMemberName}>{member.username}</h3>
                  <div className={styles.levelBadgeSmall}>
                    Level {getLevel(member.orgPoints || 0).level} - {formatLevelBadge(getLevel(member.orgPoints || 0).level)}
                  </div>
                  {member.commendationsCount !== undefined && member.commendationsCount > 0 && (
                    <div className={styles.commendationsBadgeSmall}>
                      <Medal size={14} /> {member.commendationsCount}
                    </div>
                  )}
                  {member.department && (
                    <div className={styles.listDepartment}>{member.department}</div>
                  )}
                </div>
              </div>
              <div className={styles.listItemRight}>
                <div className={styles.listPositions}>
                  {[
                    member.primaryPositionTitle,
                    member.secondaryPositionTitle,
                    member.tertiaryPositionTitle
                  ].filter(Boolean).join(', ') || 'No position assigned'}
                </div>
                <div className={styles.listJoinDate}>
                  Member since {new Date(member.joinDate).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {filteredMembers.length === 0 && (
        <div className={styles.emptyState}>
          <p>No members found matching your search criteria.</p>
        </div>
      )}
      </div>
    </div>
  );
};

export default MembersDirectory;
